/**
 * RadioSelectTable — Reusable split-pane master list table.
 *
 * Props:
 *  rows         {Array}    Full dataset (all rows).
 *  columns      {Array}    Column definitions: [{ key, header, width?, align? }]
 *                           align: 'left' | 'center' | 'right'  (default: 'left')
 *  rowKey       {string}   Field name used as a unique row identifier (e.g. 'hubId').
 *  selectedKey  {any}      The currently selected rowKey value.
 *  onSelect     {Function} Called with the full row object when a radio is clicked.
 *  searchKeys   {Array}    List of field names to include in the search filter.
 *  searchPlaceholder {string} Placeholder text for the search box.
 *  sortKey      {string}   Default column key to sort by.
 *  headerTitle  {string}   Title displayed above the table.
 *  onAdd        {Function} Callback fired when the Add (green "+") button is clicked.
 *  pageSizes    {Array}    Available page-size options (default: [5, 10, 20, 50]).
 */

import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Fab, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

const BTN_BASE = {
  border: '1px solid #e2e8f0',
  backgroundColor: '#ffffff',
  width: '32px',
  height: '32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '6px',
  fontSize: '0.85rem',
  color: '#475569',
  outline: 'none',
  boxShadow: 'none',
  padding: 0,
};

const getNestedValue = (obj, path) => {
  if (!obj || !path) return undefined;
  const cleanPath = String(path).replace(/\?\./g, '.');
  return cleanPath.split('.').reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), obj);
};

const RadioSelectTable = ({
  rows = [],
  columns = [],
  rowKey,
  selectedKey,
  onSelect,
  searchKeys = [],
  searchPlaceholder = 'Search...',
  sortKey,
  headerTitle = 'List',
  onAdd,
  pageSizes = [5, 10, 20, 50],
  filterDropdown,
  minWidth = '100%',
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortCol, setSortCol] = useState(sortKey || (columns[0]?.key ?? ''));
  const [sortAsc, setSortAsc] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageSizes[1] ?? 10);

  /* ── Filter ─────────────────────────────────────────────────── */
  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return rows;
    const q = searchQuery.toLowerCase();
    return rows.filter(row =>
      searchKeys.some(k => String(getNestedValue(row, k) ?? '').toLowerCase().includes(q))
    );
  }, [rows, searchQuery, searchKeys]);

  /* ── Sort ────────────────────────────────────────────────────── */
  const sorted = useMemo(() => {
    if (!sortCol) return filtered;
    return [...filtered].sort((a, b) => {
      const va = String(getNestedValue(a, sortCol) ?? '');
      const vb = String(getNestedValue(b, sortCol) ?? '');
      return sortAsc ? va.localeCompare(vb) : vb.localeCompare(va);
    });
  }, [filtered, sortCol, sortAsc]);

  /* ── Pagination ──────────────────────────────────────────────── */
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const paginated = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, currentPage, pageSize]);

  const handleSort = (key) => {
    if (sortCol === key) {
      setSortAsc(p => !p);
    } else {
      setSortCol(key);
      setSortAsc(true);
    }
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  /* ── Styles ──────────────────────────────────────────────────── */
  const thStyle = (col) => ({
    padding: '12px 16px',
    fontWeight: 700,
    fontSize: '0.72rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: '#475569',
    textAlign: col.align ?? 'left',
    cursor: col.sortable !== false ? 'pointer' : 'default',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    backgroundColor: '#f8fafc',
  });

  const tdStyle = (col) => ({
    padding: '12px 16px',
    fontSize: '0.700rem',
    color: '#1e293b',
    textAlign: col.align ?? 'left',
    verticalAlign: 'middle',
    whiteSpace: col.whiteSpace ?? 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  });

  return (
    <div className="card" style={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
      <div className="card-body">
        {/* ── Header row ─────────────────────────────────── */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3 className="page-title mb-0" style={{ fontWeight: 700 }}>{headerTitle}</h3>
          {onAdd && (
            <Tooltip title="Add new record">
              <Fab size="small" aria-label="add" style={{ backgroundColor: '#32CA73', color: '#fff' }} onClick={onAdd}>
                <AddIcon />
              </Fab>
            </Tooltip>
          )}
        </div>

        {/* ── Search & Filter Row ─────────────────────────── */}
        <div className="d-flex align-items-center flex-wrap gap-3" style={{ marginBottom: '16px' }}>
          <div style={{ position: 'relative', flex: '1', minWidth: '240px', maxWidth: '300px' }}>
            <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '0.9rem', pointerEvents: 'none' }}>
              🔍
            </span>
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={handleSearch}
              style={{
                width: '100%',
                paddingLeft: '32px',
                height: '38px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '0.85rem',
                outline: 'none',
                color: '#1e293b',
              }}
            />
          </div>
          {filterDropdown && (
            <div style={{ flex: '1', minWidth: '200px', maxWidth: '300px' }}>
              {filterDropdown}
            </div>
          )}
        </div>

        {/* ── Table ─────────────────────────────────────── */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth, borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup>
              {/* Fixed select column */}
              <col style={{ width: '60px' }} />
              {/* Fixed # column */}
              <col style={{ width: '50px' }} />
              {/* Data columns get their width from the column definition */}
              {columns.map(col => (
                <col key={col.key} style={{ width: col.width ?? 'auto' }} />
              ))}
            </colgroup>

            <thead>
              <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                <th style={{ ...thStyle({ align: 'center' }), cursor: 'default' }}>Select</th>
                <th style={{ ...thStyle({ align: 'center' }), cursor: 'default' }}>#</th>
                {columns.map(col => (
                  <th
                    key={col.key}
                    style={thStyle(col)}
                    onClick={() => col.sortable !== false && handleSort(col.key)}
                  >
                    {col.header}
                    {col.sortable !== false && (
                      <span style={{ marginLeft: '5px', fontSize: '0.65rem', color: sortCol === col.key ? '#3b82f6' : '#94a3b8' }}>
                        {sortCol === col.key ? (sortAsc ? '▲' : '▼') : '⇅'}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {paginated.length > 0 ? (
                paginated.map((row, idx) => {
                  const isSelected = row[rowKey] === selectedKey;
                  return (
                    <tr
                      key={row[rowKey]}
                      onClick={() => onSelect(row)}
                      style={{
                        borderBottom: '1px solid #f1f5f9',
                        cursor: 'pointer',
                        backgroundColor: isSelected ? '#eff6ff' : 'transparent',
                        transition: 'background-color 0.15s',
                      }}
                      onMouseEnter={e => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f8fafc'; }}
                      onMouseLeave={e => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      {/* Radio */}
                      <td style={{ ...tdStyle({ align: 'center' }) }}>
                        <input
                          type="radio"
                          name="radioSelectTable"
                          checked={isSelected}
                          onChange={() => onSelect(row)}
                          style={{ accentColor: '#6366f1', cursor: 'pointer', width: '16px', height: '16px' }}
                        />
                      </td>
                      {/* Row number */}
                      <td style={{ ...tdStyle({ align: 'center' }), fontWeight: 600, color: '#64748b', fontSize: '0.82rem' }}>
                        {(currentPage - 1) * pageSize + idx + 1}
                      </td>
                      {/* Data columns */}
                      {columns.map(col => {
                        const cellValue = getNestedValue(row, col.key);
                        return (
                          <td key={col.key} style={tdStyle(col)}>
                            {col.render ? col.render(cellValue, row) : (cellValue ?? '—')}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={columns.length + 2} style={{ padding: '40px 20px', textAlign: 'center', color: '#64748b', fontSize: '0.875rem' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                      <span style={{ fontSize: '0.95rem', fontWeight: 600, color: '#475569' }}>No records found.</span>
                      {onAdd && (
                        <button
                          type="button"
                          onClick={onAdd}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            backgroundColor: '#32CA73',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '8px 18px',
                            fontSize: '0.85rem',
                            fontWeight: 700,
                            cursor: 'pointer',
                            boxShadow: '0 2px 6px rgba(50, 202, 115, 0.3)',
                            transition: 'all 0.15s ease',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#2ab765'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#32CA73'; }}
                        >
                          <AddIcon style={{ fontSize: '1.1rem' }} />
                          {headerTitle ? `Add New ${headerTitle.replace(/List|Management|Accounts|Rates|Agencies/gi, '').trim() || 'Record'}` : 'Add New Record'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination footer ──────────────────────────── */}
        {sorted.length > 0 && (
          <div
            className="d-flex justify-content-between align-items-center flex-wrap gap-2"
            style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}
          >
            {/* Entry count */}
            <span style={{ fontSize: '0.82rem', color: '#64748b', whiteSpace: 'nowrap' }}>
              Showing{' '}
              <strong>{Math.min(sorted.length, (currentPage - 1) * pageSize + 1)}</strong>
              {' '}to{' '}
              <strong>{Math.min(sorted.length, currentPage * pageSize)}</strong>
              {' '}of <strong>{sorted.length}</strong> entries
            </span>

            {/* Controls */}
            <div className="d-flex align-items-center gap-2">
              {/* Pagination buttons */}
              <div className="d-flex gap-1">
                {[
                  { label: '«', action: () => setCurrentPage(1),            disabled: currentPage === 1 },
                  { label: '‹', action: () => setCurrentPage(p => Math.max(1, p - 1)),   disabled: currentPage === 1 },
                  null, // current page badge
                  { label: '›', action: () => setCurrentPage(p => Math.min(totalPages, p + 1)), disabled: currentPage === totalPages },
                  { label: '»', action: () => setCurrentPage(totalPages),   disabled: currentPage === totalPages },
                ].map((btn, i) =>
                  btn === null ? (
                    <span
                      key="current"
                      style={{ ...BTN_BASE, backgroundColor: '#3b82f6', color: '#fff', border: 'none', cursor: 'default', fontWeight: 700 }}
                    >
                      {currentPage}
                    </span>
                  ) : (
                    <button
                      key={i}
                      onClick={btn.action}
                      disabled={btn.disabled}
                      style={{ ...BTN_BASE, cursor: btn.disabled ? 'not-allowed' : 'pointer', opacity: btn.disabled ? 0.45 : 1 }}
                    >
                      {btn.label}
                    </button>
                  )
                )}
              </div>

              {/* Page size select */}
              <select
                value={pageSize}
                onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                style={{
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  height: '32px',
                  padding: '0 8px',
                  fontSize: '0.8rem',
                  color: '#475569',
                  backgroundColor: '#fff',
                  cursor: 'pointer',
                  outline: 'none',
                }}
              >
                {pageSizes.map(s => (
                  <option key={s} value={s}>Show {s}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

RadioSelectTable.propTypes = {
  rows:              PropTypes.array.isRequired,
  columns:           PropTypes.arrayOf(PropTypes.shape({
    key:      PropTypes.string.isRequired,
    header:   PropTypes.string.isRequired,
    width:    PropTypes.string,
    align:    PropTypes.oneOf(['left', 'center', 'right']),
    sortable: PropTypes.bool,
    render:   PropTypes.func,
  })).isRequired,
  rowKey:            PropTypes.string.isRequired,
  selectedKey:       PropTypes.any,
  onSelect:          PropTypes.func.isRequired,
  searchKeys:        PropTypes.arrayOf(PropTypes.string),
  searchPlaceholder: PropTypes.string,
  sortKey:           PropTypes.string,
  headerTitle:       PropTypes.string,
  onAdd:             PropTypes.func,
  pageSizes:         PropTypes.arrayOf(PropTypes.number),
  filterDropdown:    PropTypes.node,
  minWidth:          PropTypes.string,
};

export default RadioSelectTable;
